import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { BookOpen, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SeriesTracking } from '@/hooks/useSeriesTracking';

interface FindNextBookButtonProps {
  series: SeriesTracking;
}

export function FindNextBookButton({ series }: FindNextBookButtonProps) {
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  const handleFindNextBook = async () => {
    setSearching(true);
    try {
      const nextPosition = series.highest_book_position + 1;

      // Search for the next book in the series
      const { data: books, error } = await supabase
        .from('books')
        .select('id, title, series_position, cover_url')
        .ilike('series_name', series.series_name)
        .eq('series_position', nextPosition)
        .contains('authors', [series.author_name])
        .limit(5);

      if (error) throw error;

      if (books && books.length > 0) {
        // Navigate to the first matching book
        navigate(`/book/${books[0].id}`);
        toast.success(`Found: ${books[0].title}`);
      } else {
        // If not found in DB, search for it
        toast.info('Book not in database. Searching...');
        navigate(`/search?q=${encodeURIComponent(`${series.series_name_display} book ${nextPosition} ${series.author_name}`)}`);
      }
    } catch (error) {
      console.error('Error finding next book:', error);
      toast.error('Could not find next book');
    } finally {
      setSearching(false);
    }
  };

  // Don't show button if series is complete
  if (series.total_books_known && series.highest_book_position >= series.total_books_known) {
    return null;
  }

  return (
    <Button
      onClick={handleFindNextBook}
      disabled={searching}
      variant="outline"
      size="sm"
      className="w-full"
    >
      {searching ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Searching...
        </>
      ) : (
        <>
          <BookOpen className="h-4 w-4 mr-2" />
          Find Next Book (Book {series.highest_book_position + 1})
        </>
      )}
    </Button>
  );
}
